/** Batch "Reimport" dialog for the api-requests grid (a custom `dialog(ctx)`
 *  batch action) — the console counterpart of the `apireq reimport`
 *  management command + the Django admin action.
 *
 *  Each selected request's stored webhook body is replayed through its
 *  integration's webhook entrypoint. Requests without an integration are
 *  skipped. Failures stamp an error series onto the request (searchable via
 *  `errors=yes`); successes clear it.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import { useState } from 'react';

import type { ReimportParams } from '@/api/apiRequests';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

const NOTIFICATION_CHOICES: { value: ReimportParams['send_notifications']; label: string }[] = [
  { value: 'default', label: "Integration's own config" },
  { value: 'send', label: 'Force ON for this replay' },
  { value: 'skip', label: 'Force OFF for this replay' },
];

export function ReimportApiRequestsDialog({ ctx }: { ctx: BatchActionContext }) {
  const [sendNotifications, setSendNotifications] =
    useState<ReimportParams['send_notifications']>('default');
  const [includePast, setIncludePast] = useState(false);
  const [ignoreIfFutureUpdates, setIgnoreIfFutureUpdates] = useState(false);
  const [force, setForce] = useState(false);

  const submit = () => {
    const params: ReimportParams = {
      send_notifications: sendNotifications,
      include_past: includePast,
      ignore_if_future_updates: ignoreIfFutureUpdates,
      force,
    };
    void ctx.runStandard({ ...params });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Reimport API requests</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="warning">
            Each selected request&apos;s stored webhook body is replayed through its integration
            — bookings are re-created/updated as if the webhook arrived again. Requests without
            an integration are skipped. Capped at 500 rows per batch; runs in the background.
          </Alert>

          <FormControl size="small" fullWidth>
            <InputLabel id="reimport-notifications-label">Guest notifications</InputLabel>
            <Select
              labelId="reimport-notifications-label"
              label="Guest notifications"
              value={sendNotifications}
              onChange={(e) =>
                setSendNotifications(e.target.value as ReimportParams['send_notifications'])
              }
            >
              {NOTIFICATION_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Whether changes made by the replay send guest invitations/notifications.
            </FormHelperText>
          </FormControl>

          <FormControlLabel
            control={
              <Switch checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} />
            }
            label="Include requests whose bookings are all in the past"
          />
          <FormControlLabel
            control={
              <Switch
                checked={ignoreIfFutureUpdates}
                onChange={(e) => setIgnoreIfFutureUpdates(e.target.checked)}
              />
            }
            label="Skip a request when a newer request exists for the same booking"
          />
          <FormControlLabel
            control={<Switch checked={force} onChange={(e) => setForce(e.target.checked)} />}
            label="Force processing (bypass the integration's skip heuristics)"
          />

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} requests (up to the 500-row
              cap).
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={submit}>
          Reimport {ctx.count.toLocaleString()} request(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
